import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowRight, Calendar, PlayCircle } from 'lucide-react';
import { AnimatedSection, AnimatedCard } from '../components/AnimatedSection';

const VSLFunnel = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handlePlayClick = () => {
    const video = videoRef.current;
    if (video) {
      video.play()
        .then(() => {
          setIsPlaying(true);
          video.setAttribute('controls', 'controls');
        })
        .catch((err) => {
          console.error('Video play failed:', err);
          video.muted = true;
          video.play()
            .then(() => {
              setIsPlaying(true);
              video.setAttribute('controls', 'controls');
            })
            .catch((e) => console.error('Muted play also failed:', e));
        });
    }
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);
  };

  const scrollToCalendly = () => {
    document.getElementById('calendly-section').scrollIntoView({ 
      behavior: 'smooth' 
    });
  };

  const testimonials = [
    {
      name: "Matt",
      image: "/matt-transformation.jpg",
      alt: "Matt transformation - before and after"
    },
    {
      name: "Jack",
      image: "https://customer-assets.emergentagent.com/job_working-men-gains/artifacts/wotcn5uh_jack%20testimonial.jpeg",
      alt: "Jack transformation - before and after"
    },
    {
      name: "Mario",
      image: "https://customer-assets.emergentagent.com/job_working-men-gains/artifacts/okv01bvp_Mario%20transformation%20.jpeg",
      alt: "Mario transformation - before and after"
    }
  ];

  return (
    <div className="vsl-funnel">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className={`hero-text ${isVisible ? 'fade-in' : ''}`}>
            <p className="preheadline">Attention: Men Working 12+ Hour Shifts</p>
            <h1 className="headline">
              We Help Men Working Long Hours Get Lean, Strong, and Visibly Muscular Within 3 Months With a Plan Built Around Their Schedule
            </h1>
            <p className="subheadline">
              Find out how men working brutal hours are finally losing the gut, building real size, and looking like they actually lift using a system built for the way they work.
            </p>
          </div>

          {/* VSL Video */}
          <div className={`vsl-video-container ${isVisible ? 'slide-up' : ''}`}>
            <div className="video-embed-wrapper">
              <div className="custom-video-player">
                <video 
                  ref={videoRef}
                  id="vsl-video"
                  className="vsl-video-player"
                  playsInline
                  preload="auto"
                  poster="https://customer-assets.emergentagent.com/job_working-men-gains/artifacts/px146l9j_VSL%20thumbnail2.png"
                  onPlay={handleVideoPlay}
                >
                  <source 
                    src="/vsl-video.mp4" 
                    type="video/mp4" 
                  />
                  Your browser does not support the video tag.
                </video>
                {!isPlaying && (
                  <div 
                    className="play-button-overlay" 
                    onClick={handlePlayClick}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="play-button-circle">
                      <PlayCircle className="play-button-icon" size={70} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Button 
            onClick={scrollToCalendly}
            className="cta-button"
            size="lg"
          >
            Book My Free Strategy Call
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="testimonials-container">
          <AnimatedSection animation="fadeUp">
            <h2 className="section-headline">Real Results From Men Working Brutal Hours</h2>
          </AnimatedSection>
          
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <AnimatedCard 
                key={index} 
                index={index}
                className="testimonial-card-wrapper"
              >
                <Card className="testimonial-card">
                  <div className="testimonial-image-wrapper">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.alt}
                      className="testimonial-image"
                    />
                  </div>
                </Card>
              </AnimatedCard>
            ))}
          </div>

          {/* Talking Testimonial Video */}
          <AnimatedSection animation="fadeUp" delay={0.2}>
            <div className="talking-testimonial-container">
              <h3 className="talking-testimonial-title">Hear From Our Clients</h3>
              <div className="talking-video-wrapper">
                <video 
                  className="testimonial-video"
                  controls
                  playsInline
                  preload="metadata"
                  poster="/zac-testimonial-thumbnail.jpg"
                >
                  <source 
                    src="https://customer-assets.emergentagent.com/job_working-men-gains/artifacts/circr1a0_Zac%20Testimonial.MP4" 
                    type="video/mp4" 
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection animation="fadeUp" delay={0.3}>
            <Button 
              onClick={scrollToCalendly}
              className="cta-button"
              size="lg"
            >
              Book My Free Strategy Call
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </AnimatedSection>
        </div>
      </section>

      {/* Calendly Section */}
      <section id="calendly-section" className="calendly-section">
        <div className="calendly-container">
          <AnimatedSection animation="fadeUp">
            <div className="calendly-header">
              <Calendar className="calendly-icon" size={48} />
              <h2 className="calendly-title">Book Your Free Strategy Call</h2>
              <p className="calendly-subtitle">
                Let's discuss how we can help you transform your physique around your work schedule
              </p>
            </div>
          </AnimatedSection>
          
          <AnimatedSection animation="scale" delay={0.2}>
            <div className="calendly-embed-container">
              <iframe
                src="https://calendly.com/rudi-rudigarciacoaching/1-1-coaching-strategy-call"
                width="100%"
                height="700"
                frameBorder="0"
                title="Book a Strategy Call"
                className="calendly-iframe"
              ></iframe>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <h3 className="footer-brand">Rudi Garcia Coaching</h3>
          <p className="footer-disclaimer">
            This site is not a part of the Facebook website or Facebook Inc. Additionally, 
            this site is not endorsed by Facebook in any way. Facebook is a trademark of Facebook, Inc.
          </p>
          <p className="footer-copyright">
            Copyright Rudi Garcia Coaching | All Rights Reserved
          </p>
        </div>
      </footer>
    </div>
  );
};

export default VSLFunnel;
